import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

export interface EngagementState {
  userId: string;
  hoursInactive: number;
  inactivityTier: number;
  engagementScore: number;
  moodState: "thriving" | "steady" | "struggling" | "at_risk" | "churning";
  consecutiveInactiveDays: number;
  lastActive: string | null;
  lastActions: {
    weight: string | null;
    habit: string | null;
    fasting: string | null;
    exercise: string | null;
    outdoor: string | null;
    journal: string | null;
  };
  streakInfo: {
    login_streak: number;
    habit_streak: number;
    longest_streak: number;
  };
  progressSummary: {
    lastWeight: number | null;
    weightChange7d: number;
    interactions7d: number;
  };
  notificationHistory: {
    lastSent: string | null;
    lastTier: number;
    sentThisWeek: number;
    responseRate: number;
  };
  personality: string;
  userName: string | null;
  typicalActiveHours: { start: number; end: number };
}

export const useEngagementState = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: engagementState, isLoading, error } = useQuery({
    queryKey: ["engagement-state", user?.id],
    queryFn: async (): Promise<EngagementState | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase.rpc("get_user_encouragement_context", {
        p_user_id: user.id
      });

      if (error) {
        console.error("Error fetching engagement state:", error);
        throw error;
      }

      return data as unknown as EngagementState;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    refetchOnWindowFocus: true,
  });

  const updateActivity = useMutation({
    mutationFn: async (actionType: string) => {
      if (!user?.id) throw new Error("No user");

      const { error } = await supabase.rpc("update_user_engagement_state", {
        p_user_id: user.id,
        p_action_type: actionType,
        p_action_value: null
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["engagement-state", user?.id] });
    }
  });

  const recordNotificationInteraction = useMutation({
    mutationFn: async (params: {
      notificationId?: string;
      action: "opened" | "dismissed" | "led_to_action";
      actionType?: string;
    }) => {
      if (!user?.id) throw new Error("No user");

      const updateData: Record<string, unknown> = {};
      
      if (params.action === "opened") {
        updateData.opened_at = new Date().toISOString();
      } else if (params.action === "dismissed") {
        updateData.dismissed_at = new Date().toISOString();
      } else if (params.action === "led_to_action") {
        updateData.led_to_action = true;
        updateData.action_type = params.actionType;
        // Calculate time from notification sent to action
        // This will be calculated by the DB based on sent_at
      }

      // Find and update the most recent notification interaction for this user
      const { data: recentNotification } = await supabase
        .from("notification_interactions")
        .select("id, sent_at")
        .eq("user_id", user.id)
        .order("sent_at", { ascending: false })
        .limit(1)
        .single();

      if (recentNotification) {
        // Calculate action_within_minutes if led to action
        if (params.action === "led_to_action" && recentNotification.sent_at) {
          const sentAt = new Date(recentNotification.sent_at);
          const now = new Date();
          const minutesDiff = Math.round((now.getTime() - sentAt.getTime()) / (1000 * 60));
          updateData.action_within_minutes = minutesDiff;
        }

        const { error } = await supabase
          .from("notification_interactions")
          .update(updateData)
          .eq("id", recentNotification.id);

        if (error) throw error;
      }
    }
  });

  // Helper to get mood-appropriate messaging style
  const getMoodGuidance = () => {
    if (!engagementState) return null;

    const guidance = {
      thriving: {
        tone: "celebratory",
        approach: "Celebrate wins, encourage ambitious goals",
        samplePrompt: "Your momentum is incredible! Ready to level up?"
      },
      steady: {
        tone: "encouraging",
        approach: "Acknowledge consistency, maintain motivation",
        samplePrompt: "Great rhythm! Keep it going."
      },
      struggling: {
        tone: "gentle",
        approach: "Non-judgmental, focus on small wins",
        samplePrompt: "Every step counts. What's one small thing today?"
      },
      at_risk: {
        tone: "empathetic",
        approach: "Low pressure, just express care",
        samplePrompt: "Just checking in. No pressure - I'm here when you're ready."
      },
      churning: {
        tone: "welcoming",
        approach: "Warm welcome back, extremely low pressure",
        samplePrompt: "Welcome back! The door's always open."
      }
    };

    return guidance[engagementState.moodState];
  };

  // Check if we should show proactive messages
  const shouldShowProactiveMessage = () => {
    if (!engagementState) return false;
    
    // Don't show if they've been very recently active
    if (engagementState.hoursInactive < 4) return false;
    
    // Don't show too many notifications per week
    if (engagementState.notificationHistory.sentThisWeek >= 3) return false;
    
    // Check if within their typical active hours
    const currentHour = new Date().getHours();
    const { start, end } = engagementState.typicalActiveHours;
    if (currentHour < start || currentHour > end) return false;
    
    return true;
  };

  return {
    engagementState,
    isLoading,
    error,
    updateActivity: updateActivity.mutate,
    recordNotificationInteraction: recordNotificationInteraction.mutate,
    getMoodGuidance,
    shouldShowProactiveMessage
  };
};
